// MessageContent.js - Shared component for rendering messages with syntax-highlighted code
// Used in both main chat (App.js) and Course Content view

import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CodeRunnerModal from './CodeRunnerModal';

// Helper to normalize Python code indentation
// Only fixes code where ALL non-empty lines have zero indentation (completely flat code)
// If ANY line has proper indentation, the LLM got it right - leave it alone
const normalizeIndentation = (code) => {
  if (!code || !code.trim()) return code;
  
  const lines = code.split('\n');
  
  // Check if ANY non-empty line (except the first) has leading whitespace
  // If so, the code already has indentation structure - preserve it
  let hasAnyIndentation = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed.length > 0 && line.startsWith(' ')) {
      hasAnyIndentation = true;
      break;
    }
  }
  
  // If code already has some indentation, return as-is
  if (hasAnyIndentation) {
    return code;
  }
  
  // Code has NO indentation at all (all lines start at column 0)
  // This means the LLM stripped indentation - we need to rebuild it
  const result = [];
  let currentIndentLevel = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    if (trimmed.length === 0) {
      result.push('');
      continue;
    }
    
    // Remove comments to check for block openers
    const codeWithoutComment = trimmed.replace(/#.*$/, '').trim();
    
    // Check if current line is a dedent keyword
    const isDedentKeyword = /^(else:|elif\s|except:|except\s|finally:)/.test(trimmed);
    
    if (isDedentKeyword && currentIndentLevel >= 4) {
      currentIndentLevel -= 4;
    }
    
    result.push(' '.repeat(currentIndentLevel) + trimmed);
    
    if (codeWithoutComment.endsWith(':')) {
      currentIndentLevel += 4;
    }
  }
  
  return result.join('\n');
};

// Component to render a message with proper code highlighting
const MessageContent = ({ content, showTryIt = false, onSendToChat = null }) => {
  const [runnerCode, setRunnerCode] = useState(null); // Code to run in modal

  // Check if we should show Try It button for this code block
  // Only show for code blocks AFTER "Code Examples" section and not labeled as output
  const shouldShowTryIt = (allPrecedingText, recentText) => {
    if (!allPrecedingText) return false;
    
    const lowerAll = allPrecedingText.toLowerCase();
    
    // Must be after "Code Examples" section (check various formats)
    const hasCodeExamples = lowerAll.includes('code examples') || 
                           lowerAll.includes('code example:') ||
                           lowerAll.includes('**code examples**') ||
                           lowerAll.includes('## code');
    
    if (!hasCodeExamples) return false;
    
    // Check recent text (immediately before this code block) - if it mentions "output", skip
    if (recentText && recentText.toLowerCase().slice(-150).includes('output')) return false;
    
    return true; // Show Try It button
  };

  if (!content) return null;
  
  // Split content by code blocks
  const parts = [];
  let lastIndex = 0;
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  let match;
  
  while ((match = codeBlockRegex.exec(content)) !== null) {
    // Add text before code block
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex, match.index)
      });
    }
    // Add code block - preserve original formatting from LLM
    parts.push({
      type: 'code',
      language: match[1] || 'python',
      content: match[2].trim()
    });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      content: content.slice(lastIndex)
    });
  }
  
  // If no parts, treat whole content as text
  if (parts.length === 0) {
    parts.push({ type: 'text', content: content });
  }
  
  // Format text parts (bold, inline code, headers, newlines)
  const formatText = (text) => {
    let result = text;
    // Check if content already has KaTeX HTML (pre-rendered math) - don't escape it
    const hasKatex = result.includes('class="katex"');
    
    if (!hasKatex) {
      // Escape HTML only if no KaTeX present
      result = result.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    // Bold
    result = result.replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #58a6ff;">$1</strong>');
    // Inline code
    result = result.replace(/`([^`]+)`/g, '<code style="background-color: #161b22; padding: 2px 6px; border-radius: 4px; font-family: Consolas, Monaco, monospace; font-size: 13px; color: #f0ad4e;">$1</code>');
    // Headers
    result = result.replace(/^## (.+)$/gm, '<h3 style="color: #58a6ff; margin: 16px 0 8px 0; font-size: 16px;">$1</h3>');
    result = result.replace(/^### (.+)$/gm, '<h4 style="color: #8b949e; margin: 12px 0 6px 0; font-size: 14px;">$1</h4>');
    // Note: Don't convert \n to <br> - we use white-space: pre-wrap to preserve formatting
    return result;
  };
  
  return (
    <div>
      {parts.map((part, index) => {
        if (part.type === 'code') {
          // Get ALL preceding text up to this point to check for "Code Examples:" section
          const allPrecedingText = parts
            .slice(0, index)
            .filter(p => p.type === 'text')
            .map(p => p.content)
            .join('');
          
          // Get just the immediately preceding text part to check for "output" label
          const precedingPart = index > 0 ? parts[index - 1] : null;
          const recentText = precedingPart?.type === 'text' ? precedingPart.content : '';
          
          return (
            <div key={index}>
              <SyntaxHighlighter
                language={part.language}
                style={vscDarkPlus}
                customStyle={{
                  margin: '10px 0 0 0',
                  padding: '12px 16px',
                  borderRadius: showTryIt ? '8px 8px 0 0' : '8px',
                  fontSize: '15px',
                  lineHeight: '1.5',
                  border: '1px solid #30363d',
                  borderBottom: showTryIt ? 'none' : '1px solid #30363d'
                }}
                showLineNumbers={part.content.split('\n').length > 3}
              >
                {part.content}
              </SyntaxHighlighter>
              {showTryIt && (part.language === 'python' || part.language === '' || !part.language) && shouldShowTryIt(allPrecedingText, recentText) && (
                <button
                  onClick={() => setRunnerCode(part.content)}
                  style={{
                    width: '100%',
                    padding: '8px 14px',
                    backgroundColor: '#21262d',
                    color: '#79c0ff',
                    border: '1px solid #30363d',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    marginBottom: '10px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#30363d'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#21262d'}
                >
                  ▶️ Try It
                </button>
              )}
            </div>
          );
        } else {
          return (
            <span
              key={index}
              style={{ whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ __html: formatText(part.content) }}
            />
          );
        }
      })}

      {/* Code Runner Modal */}
      {runnerCode !== null && (
        <CodeRunnerModal
          initialCode={runnerCode}
          title="Try It Out"
          onClose={() => setRunnerCode(null)}
          onSendToChat={onSendToChat ? (message) => {
            setRunnerCode(null);
            onSendToChat(message);
          } : null}
        />
      )}
    </div>
  );
};

export default MessageContent;
